
import { defineComponent, ref, reactive, onMounted, onBeforeUnmount, computed, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'

import { useHead } from '@vueuse/head'

import { RepositoryFactory } from '@/repositories/RepositoryFactory'
const SeasonGameRepository = RepositoryFactory.get('seasonGame')
const SeasonGameEventRepository = RepositoryFactory.get('seasonGameEvent')
const SeasonEntryRosterRepository = RepositoryFactory.get('seasonEntryRoster')

import BaseDialog from '@/components/BaseDialog.vue'
import ScoreBoard from '@/components/seasonGameScoreBoard/ScoreBoard.vue'
import GameStartModal from '@/components/seasonGameScoreBoard/GameStartModal.vue'

import { Dialog } from '@/types/dialog'
import { Game } from '@/types/game'
import { GameEvent } from '@/types/gameEvent'
import { GameEventForm } from '@/types/gameEventForm'
import { RosterPlayer } from '@/types/rosterPlayer'

export default defineComponent({
  name: 'SeasonGameScoreBoard',
  inheritAttrs: false,
  components: {
    BaseDialog,
    ScoreBoard,
    GameStartModal
  },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const store = useStore()

    onMounted(() => {
      getGame()
      getGameEvents()

      window.addEventListener('pagehide', () => {
        setTimerOnLocalStorage()
      })

      nextTick(() => {
        const timerJson = localStorage.getItem('gameTimer' + route.params.game_id)

        if (!timerJson) return

        const timerObj = JSON.parse(timerJson)

        timer.timestamp = timerObj.timestamp
        timer.stringTime = timerObj.stringTime
        timer.periodStatus = timerObj.periodStatus
        timer.isRestart = timerObj.isRestart
        timer.isCounting = timerObj.isCounting

        if (timer.isCounting) startTimer()
      })
    })

    onBeforeUnmount(() => {
      window.removeEventListener('pagehide', () => {
        setTimerOnLocalStorage()
      })

      setTimerOnLocalStorage()
    })

    const setTimerOnLocalStorage = () => {
      const timerJson = JSON.stringify(timer)
      localStorage.setItem('gameTimer' + route.params.game_id, timerJson)
    }

    const game: {
      data?: Game
    } = reactive({
      data: undefined
    })

    const getGame = async () => {
      try {
        const { season_id, game_id } = route.params

        const res = await SeasonGameRepository.getGame(season_id, game_id)

        if (res.status === 200) {
          const r = res.data
          const data = r.data

          game.data = data.game

          if (game.data?.home.id != undefined && game.data?.away.id != undefined) {
            getHomeRosters(game.data?.home.id)
            getAwayRosters(game.data?.away.id)
          }
        }
      } catch (error) {
        //
      }
    }

    const startGame = async () => {
      try {
        const { season_id, game_id } = route.params

        const res = await SeasonGameRepository.startGame(season_id, game_id)

        if (res.status === 200) {
          getGame()
        }
      } catch (error) {
        //
      }
    }

    const stopGame = async () => {
      try {
        const { season_id, game_id } = route.params

        const dataObj = {
          game_status: timer.periodStatus
        }

        const res = await SeasonGameRepository.stopGame(season_id, game_id, dataObj)

        if (res.status === 200) {
          const { topic_id, season_id, game_id } = route.params

          router.push({
            name: 'seasonGameDetail',
            params: { topic_id: topic_id, season_id: season_id, game_id: game_id },
            query: { group: game.data?.group_id }
          })
        }
      } catch (error) {
        //
      }
    }

    const homeTotalScore = computed(() => {
      if (game.data == undefined) return 0

      let homeScores: any[] = [
        game.data.option?.home_1st_score,
        game.data.option?.home_2nd_score,
        game.data.option?.home_ex_1st_score,
        game.data.option?.home_ex_2nd_score
      ]

      homeScores = homeScores.map((score: number | null) => Number(score))
      return homeScores.reduce((a: number, b:number) => a + b)
    })

    const awayTotalScore = computed(() => {
      if (game.data == undefined) return 0

      let awayScores: any[] = [
        game.data.option?.away_1st_score,
        game.data.option?.away_2nd_score,
        game.data.option?.away_ex_1st_score,
        game.data.option?.away_ex_2nd_score
      ]

      awayScores = awayScores.map((score: number | null) => Number(score))
      return awayScores.reduce((a: number, b:number) => a + b)
    })

    const timer = reactive({
      timestamp: {
        start: 0,
        current: 0,
        passed: 0,
        periodStop: 0,
        total: 0,
      },

      stringTime: {
        currentPeriod: '00:00',

        periods: [] as string[],

        total: ''
      },

      periodStatus: 0,

      isRestart: false,
      isCounting: false
    })

    const handleTimer = (status: number, type: 'start' | 'end' | 'pk') => {
      timer.periodStatus = status
      timer.isCounting = type === 'start' ? true : false

      if (timer.isCounting) {
        startTimer()
      }

      if (type === 'start') {
        timer.stringTime.currentPeriod = '00:00'
        timer.isCounting = true

        if (timer.periodStatus === 1) startGame()
      } else if (type === 'end') {
        timer.isCounting = false
        timer.stringTime.periods.push(timer.stringTime.currentPeriod)
        clearTimer()
      }
    }

    const countUpTime = () => {
      const now = new Date()
      timer.timestamp.current = now.getTime()
      timer.timestamp.passed = timer.timestamp.current - timer.timestamp.start

      const sec: number = Math.floor(timer.timestamp.passed / 1000) % 60
      const stringSec: string = ('0' + sec).slice(-2)
      let min: number = Math.floor(timer.timestamp.passed / 1000 / 60) % 60
      const hours = Math.floor(timer.timestamp.passed / 1000 / 60 / 60) % 24
      min = hours * 60 + min
      const stringMin = ('0' + min).slice(-2)

      const count = [stringMin, stringSec]

      return count
    }

    const startTimer = () => {
      if (timer.isRestart) {
        //
      } else {
        const nowDate = new Date()
        timer.timestamp.start = nowDate.getTime()
      }

      timer.isRestart = true

      recalcTime()
    }

    const recalcTime = () => {
      if (timer.isCounting) {
        const counter = countUpTime()
        timer.stringTime.currentPeriod = `${counter[0]}:${counter[1]}`

        refreshTime()
      }
    }

    const refreshTime = () => {
      setTimeout(recalcTime, 1000)
    }

    const clearTimer = () => {
      timer.isRestart = false
    }

    const getStringTotalTime = (periods: string[]) => {
      const periodTimestamps = []

      for (let i=0; i < periods.length; i++) {
        let min = Number(periods[i].slice(0, 2))
        let sec = Number(periods[i].slice(-2))

        min = 1000 * 60 * min * 1
        sec = 1000 * sec * 1

        periodTimestamps.push(min + sec)
      }

      timer.timestamp.total = periodTimestamps.reduce((sum: number, element: number) => sum + element, 0)

      const sec = Math.floor(timer.timestamp.total / 1000) % 60
      const stringSec = ('0' + sec).slice(-2)
      let min = Math.floor(timer.timestamp.total / 1000 / 60) % 60
      const hours = Math.floor(timer.timestamp.total / 1000 / 60 / 60) % 24
      min = hours * 60 + min
      const stringMin = ('0' + min).slice(-2)

      return stringMin + ':' + stringSec
    }

    const gameDialogEnd = reactive<Dialog>({
      title: '試合を終了しますか？',
      message: '試合終了後にスコア内容を変更したい場合は、試合の流れから変更ができます。',
      confirmText: '試合を終了する',
      cancelText: 'キャンセル',

      isOpen: false
    })

    const openGameDialogEnd = () => {
      gameDialogEnd.isOpen = true
    }

    const closeGameDialogEnd = () => {
      gameDialogEnd.isOpen = false
    }

    const onClickGameEnd = () => {
      stopGame()
      closeGameDialogEnd()
    }

    const events: {
      home: GameEvent[]
      away: GameEvent[]

      selected?: GameEvent
    } = reactive({
      home: [],
      away: [],

      selected: undefined
    })

    const getGameEvents = async () => {
      try {
        const { season_id, game_id } = route.params

        const res = await SeasonGameEventRepository.getGameEvents(season_id, game_id)

        if (res.status === 200) {
          const r = res.data
          const data = r.data

          events.home = data.home_events
          events.away = data.away_events
        }
      } catch (error) {
        //
      }
    }

    const homeEventTable = ref()
    const awayEventTable = ref<HTMLElement>()

    const addGameEvent = async (event_id: number, teamType: string) => {
      try {
        const { season_id, game_id } = route.params

        const entry_team_id = teamType === 'home' ? game.data?.home?.id : game.data?.away?.id

        let periods = timer.stringTime.periods.concat()
        periods.push(timer.stringTime.currentPeriod)

        let time
        if (timer.periodStatus === 5) {
          time = 'PK'
        } else {
          time = getStringTotalTime(periods)
        }

        const dataObj: GameEventForm = {
          entry_team_id: entry_team_id as number,
          event_id: event_id,
          player_id: undefined,
          value: '',
          time: time,
          game_status: timer.periodStatus
        }

        const res = await SeasonGameEventRepository.postGameEvent(season_id, game_id, dataObj)

        if (res.status === 200) {
          const r = res.data
          const data = r.data

          if (teamType === 'home') {
            events.home.push(data.new)
          } else if (teamType === 'away') {
            events.away.push(data.new)
          }

          getGame()

          store.dispatch('addFlashMessage', {
            type: 'success',
            message: 'イベントを追加しました'
          })
        }
      } catch (error) {
        //
      }
    }

    const updateGameEvent = async (event: GameEvent) => {
      try {
        const { season_id, game_id } = route.params

        const dataObj: GameEventForm = {
          game_event_id: event.id,
          player_id: event.player_id,
          value: event.value,
          time: event.time,
        }

        const res = await SeasonGameEventRepository.updateGameEvent(season_id, game_id, dataObj)

        if (res.status === 200) {
          store.dispatch('addFlashMessage', {
            type: 'success',
            message: 'イベントを更新しました'
          })
        }
      } catch (error) {
        //
      }
    }

    const eventDialogDelete = reactive<Dialog>({
      title: '{イベント名}を削除しますか？',
      message: '削除したイベントは元に戻すことができません。',
      confirmText: '削除する',
      cancelText: 'キャンセル',

      isOpen: false
    })

    const openEventDialogDelete = (event: GameEvent) => {
      events.selected = event
      eventDialogDelete.title = event.event_name + ' を削除しますか？'

      eventDialogDelete.isOpen = true
    }

    const closeEventDialogDelete = () => {
      eventDialogDelete.isOpen = false
    }

    const deleteGameEvent = async () => {
      try {
        const { season_id, game_id } = route.params

        const dataObj = {
          game_event_id: events.selected?.id
        }

        const res = await SeasonGameEventRepository.deleteGameEvent(season_id, game_id, dataObj)

        if (res.status === 200) {
          getGameEvents()
          getGame()

          closeEventDialogDelete()
          store.dispatch('addFlashMessage', {
            type: 'success',
            message: 'イベントを削除しました'
          })
        }
      } catch (error) {
        //
      }
    }

    const rosters: {
      home: RosterPlayer[]
      away: RosterPlayer[]
    } = reactive({
      home: [],
      away: []
    })

    const getHomeRosters = async (entry_team_id?: number) => {
      try {
        const { season_id } = route.params

        const res = await SeasonEntryRosterRepository.getRosters(season_id, entry_team_id)

        if (res.status === 200) {
          const r = res.data
          const data = r.data

          rosters.home = data.rosters
        }
      } catch (error) {
        //
      }
    }

    const getAwayRosters = async (entry_team_id?: number) => {
      try {
        const { season_id } = route.params

        const res = await SeasonEntryRosterRepository.getRosters(season_id, entry_team_id)

        if (res.status === 200) {
          const r = res.data
          const data = r.data

          rosters.away = data.rosters
        }
      } catch (error) {
        //
      }
    }

    const scoreTab = reactive({
      id: 'home'
    })

    const changeScoreTab = (teamType: 'home' | 'away') => {
      scoreTab.id = teamType
    }

    onMounted(() => {
      mediaQuery.list = window.matchMedia('(max-width: 560px)')
      mediaQuery.list.addEventListener('change', setDeviceWidth)
      setDeviceWidth()
    })

    const mediaQuery: {
      list?: MediaQueryList
      isMobile: boolean
    } = reactive({
      list: undefined,
      isMobile: false
    })

    const setDeviceWidth = (): void => {
      if (mediaQuery.list?.matches) {
        mediaQuery.isMobile = true
      } else {
        mediaQuery.isMobile = false
      }
    }

    useHead({
      title: computed(() => 'スコアボード'),
    })

    return {
      game,
      getGame,
      startGame,
      stopGame,

      homeTotalScore,
      awayTotalScore,

      timer,
      handleTimer,
      startTimer,
      countUpTime,
      recalcTime,
      refreshTime,
      clearTimer,
      getStringTotalTime,

      gameDialogEnd,
      openGameDialogEnd,
      closeGameDialogEnd,
      onClickGameEnd,

      events,
      getGameEvents,

      homeEventTable,
      awayEventTable,

      addGameEvent,
      updateGameEvent,

      eventDialogDelete,
      openEventDialogDelete,
      closeEventDialogDelete,
      deleteGameEvent,

      rosters,
      getHomeRosters,
      getAwayRosters,

      scoreTab,
      changeScoreTab,
      mediaQuery
    }
  }
})
